import React, { Fragment, type FunctionComponent, useState, useLayoutEffect, useMemo } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { type LazyQueryExecFunction } from '@apollo/client';
import HelpIcon from '@mui/icons-material/HelpOutlineRounded';
import IconAdd from '@mui/icons-material/Add';
import { useLocation } from 'react-router-dom';

import useTimezoneUtils from 'hooks/useTimezoneUtils';
import { CaseModelInfoDialog } from '../../../../components/CaseModelInfoDialog';
import { Spinner } from 'components/Shared';
import { Card } from 'components/Shared/Card';
import { Button } from 'components/Shared/Inputs';
import { getPreferredPhoneNumber } from 'components/CaseManagement/utils/activityDetailsUtils';
import { type COUNTRY_CODE, regionsToShowPreferredNumber, useProviderCountryCode } from 'utils/regionUtils';
import { useAuth } from 'components/Auth';
import { checkIfStaffProvider } from 'utils';
import { ClientContactForm, getLabelsAndRulesByRegion } from '../../ClientContactForm/ClientContactForm';
import { useMutationWithExpiration } from 'providers/Apollo/useMutationWithExpiration';
import { type ActivityQueryData, UPDATE_CLIENT_CONTACT_ADDRESS } from 'components/CaseManagement/ActivityDetails/queries';
import { type IActivityDetails, type IContactAddress } from '../../../../interfaces';
import { formatPhoneNumber } from 'utils/formatter';
import { useIsEmergencyContactEnabled } from 'hooks/useIsEmergencyContactEnabled';
import { LabelValue } from 'components/Shared/LabelValue/LabelValue';

import './ClientDetails.scss';

interface ClientDetailsProps {
    activity: IActivityDetails,
    refetchActivity?: LazyQueryExecFunction<ActivityQueryData, { activityId: number; }>;
}

export const ClientDetails: FunctionComponent<ClientDetailsProps> = ({ activity, refetchActivity }) => {
    const { t, ready: translationsReady } = useTranslation(['clientdetails', 'calendaring', 'caseModelDescriptions'], { useSuspense: false });
    const { t: t2, ready: translationsReady2 } = useTranslation('emergencyContact', { useSuspense: false });
    const { hash } = useLocation();
    const [editingAddress, setEditingAddress] = useState(false);
    const isNewActivity = useIsEmergencyContactEnabled(activity);
    const [updateContactAddress, updateContactAddressMutation] = useMutationWithExpiration(UPDATE_CLIENT_CONTACT_ADDRESS, {
        onCompleted() {
            refetchActivity?.({ fetchPolicy: "network-only" }).then(() => setEditingAddress(false))
        }
    });

    const handleAddressUpdate = async (payload: IContactAddress) => {
        const { clientAddressDeclined, ...addressInfo } = payload;
        return updateContactAddress({
            variables: {
                activityId: activity?.activityId,
                providerId: activity?.providerId,
                clientId: activity?.client?.clientId,
                clientAddressDeclined: clientAddressDeclined ?? !addressInfo?.addressLine?.trim(),
                ...addressInfo
            }
        })
    }

    useLayoutEffect(() => {
        if (hash && translationsReady) {
            setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView(), 0);
        }
    }, [hash, translationsReady]);

    const { createDateFormatFunctionWithoutTimezoneConversion, DATE_PORTION_MOMENT_FORMAT } = useTimezoneUtils();

    const formatWithoutTimezone = createDateFormatFunctionWithoutTimezoneConversion(DATE_PORTION_MOMENT_FORMAT);

    const [showModelInfoDialog, setShowModelInfoDialog] = useState(false);

    const providerRegion = useProviderCountryCode(activity?.providerId);
    const fieldsByRegion = getLabelsAndRulesByRegion(t2, providerRegion);
    const { effectiveProviderData } = useAuth();
    const { contactAddress } = activity?.client ?? {};
    const isEmptyAddress = useMemo(
        () => !contactAddress || (contactAddress && Object.keys(contactAddress).filter(c => !['clientAddressDeclined','emergencyContactDeclined'].includes(c)).some(key => !contactAddress[key])), 
    [contactAddress]);

    const checkIfDisplayPreferredPhoneNumber = (region: COUNTRY_CODE) => {
        const phones = activity?.client?.phones?.map(({ number }) => number);

        const showSessionPreferredNumber = regionsToShowPreferredNumber.includes(region)
                                           && activity?.modality === 'Tele-Counselling'
                                           && activity?.service === 'Counselling'
                                           && checkIfStaffProvider(effectiveProviderData);

        if (showSessionPreferredNumber && (activity?.sessions?.length || 0) > 0) {
            return activity?.sessions?.some(session => !!session.clientReservationPhoneNumber);
        }

        if (activity?.serviceForms?.clientPhoneNumber) {
            const preferredInDefault = phones?.includes(activity?.serviceForms?.clientPhoneNumber);
            return !preferredInDefault;
        }

        return false;
    }

    const renderAdditionalParticipants = () => {
        if (activity?.additionalParticipants && activity.additionalParticipants.length > 0) {
            return (
                <>
                    <Divider />
                    <p className="global__paragraph--light additional-participants-header">{t('calendaring:additional_participants', 'Additional Participants')}</p>
                    <Grid container>
                        {activity.additionalParticipants.map(participant => (
                            <Fragment key={participant.id}>
                                <Grid item xs={6}>
                                    <LabelValue
                                        value={participant.fullName}
                                        label={participant.relationType ?? t('n_a')}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <LabelValue
                                        value={participant.dateOfBirth ? formatWithoutTimezone(participant.dateOfBirth) : t('n_a')}
                                        label={t('client_details_date_of_birth')}
                                    />
                                </Grid>
                            </Fragment>
                        ))
                        }
                    </Grid>
                </>
            )
        }
    }   
    
    const caseModelGuideDocumentUrl = t('model_guide_url', { defaultValue: '' });

    return (
        <Container maxWidth="md">
            {
                !translationsReady
                ? <Spinner fillParent/>
                : <>
                    <Card className="client_details_paper personal-details">
                        <h3>{t('client_details_personal_details')}</h3>
                        <Grid container>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={`${activity?.client?.firstName} ${activity?.client?.lastName}`}
                                    label={t('client_details_personal_full_name')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue value={activity?.client?.gender} label={t('client_details_personal_gender')} />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.client.dateOfBirth ? formatWithoutTimezone(activity.client.dateOfBirth): t('n_a')}
                                    label={t('client_details_date_of_birth')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.client?.languagePreference}
                                    label={t('client_details_personal_language')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.eligibility ?? t('n_a', 'N/A')}
                                    label={t('client_details_personal_client_type')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue value={activity?.activityId} label={t('client_details_personal_activity_id')} />
                            </Grid>    
                        </Grid>
                        {renderAdditionalParticipants()}
                    </Card>
                    <Card className="client_details_paper">
                        <h3>{t('client_details_contact_info')}</h3>
                        <Grid container>
                            {activity?.client?.phones?.map((phone, index) => (
                                <Grid item xs={6} key={index}>
                                    <LabelValue
                                        value={formatPhoneNumber(phone.number)}
                                        label={t('client_details_contact_phone_type', {
                                            type: phone.phoneTypeCode?.toLowerCase(),
                                            messageType: phone.messageType
                                        })}
                                    />                                    
                                </Grid>
                            ))}
                            {checkIfDisplayPreferredPhoneNumber(providerRegion) &&
                            <LabelValue
                                value={getPreferredPhoneNumber(activity, providerRegion, checkIfStaffProvider(effectiveProviderData))}
                                label={t('client_details_contact_phone_preferred')}
                            />}
                        </Grid>
                        <Grid container>
                            <Grid item xs={6}>
                                <LabelValue value={activity?.client?.email} label={t('client_details_contact_email')} />
                            </Grid>
                        </Grid>
                    </Card>
                    {isNewActivity && translationsReady2 && (
                        <Card className="client_details_paper">
                            <Grid container>
                                <Grid item md={10}>
                                    <h3>{t2('clients_full_address')}</h3>
                                    {!isEmptyAddress && <span>{t2('click_modify_to_edit')}</span>}
                                </Grid>
                                {!editingAddress && !isEmptyAddress && <Grid item md={2}><Button variant="opaque" onClick={() => setEditingAddress(true)}>{t2('modify')}</Button></Grid>}
                            </Grid>
                            <Grid container>
                                {isEmptyAddress && !editingAddress ? (
                                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                                        <Button size="large" style={{ margin: "32px" }} onClick={() => setEditingAddress(true)}><IconAdd />{t2('add_client_address')}</Button>                    
                                    </Grid>
                                ): editingAddress ? (
                                    <ClientContactForm viewMode='tab' busy={updateContactAddressMutation.loading} activity={activity} onSubmit={handleAddressUpdate} onCancel={() => setEditingAddress(false)} />
                                ) : (
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item md={6}>
                                                <LabelValue value={contactAddress?.addressLine} label={fieldsByRegion.addressLine.label} />
                                            </Grid>
                                            <Grid item md={6}>
                                                <LabelValue value={contactAddress?.cityName} label={fieldsByRegion.cityName.label} />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2}>
                                            <Grid item md={6}>
                                                <LabelValue value={contactAddress?.provinceStateName} label={fieldsByRegion.provinceStateName.label} />
                                            </Grid>
                                            <Grid item md={6}>
                                                <LabelValue value={contactAddress?.postalZipCode} label={fieldsByRegion.postalZipCode.label} />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Card>
                    )}
                    <Card className="client_details_paper">
                        <h3>{t('client_details_clinical_details')}</h3>
                        <p className="global__paragraph--small">
                            {t('client_details_clinical_opened_date', {
                                date: activity?.openDate ? formatWithoutTimezone(activity.openDate) : t('n_a', 'N/A')
                            })}
                        </p>
                        <Grid container>
                            <Grid item xs={6}>
                                <LabelValue value={activity?.service} label={t('client_details_clinical_service_type')} />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.presentingIssue}
                                    label={t('client_details_clinical_presenting_issue')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue value={activity?.modality} label={t('client_details_clinical_modality')} />
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.assessmentsScores?.PHQ9?.[0]?.score?.[0] ?? t('n_a', 'N/A')}
                                    label={t('client_details_clinical_PHQ9_first')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.assessmentsScores?.PHQ9?.[0]?.score?.[1] ?? t('n_a', 'N/A')}
                                    label={t('client_details_clinical_PHQ9_second')}
                                />
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.urgencyLevelString ?? t('n_a', 'N/A')}
                                    label={t('client_details_clinical_risk_level')}
                                />
                            </Grid>
                            <Grid item xs={6}></Grid>
                        </Grid> 
                    </Card>                    
                    <Card className='client_details_paper' id="organization_details">
                        <h3>{t('client_details_organization_details')}</h3>
                        <Grid container>
                            <Grid item xs={6}>
                                <LabelValue value={activity?.organization?.name} label={t('client_details_organization_name')} />
                                <LabelValue
                                    value={<>
                                        {
                                            caseModelGuideDocumentUrl
                                            ? <a href={caseModelGuideDocumentUrl} target="_blank" rel="noopener noreferrer">
                                                {activity?.sessionInfo?.modelId}
                                            </a>
                                            : <span>{activity?.sessionInfo?.modelId}</span>
                                        }
                                        
                                        <Button round variant="text" onClick={() => setShowModelInfoDialog(true)} aria-label="Click for additional information regarding model"
                                            size="small"
                                        >
                                            <HelpIcon/>
                                        </Button>
                                    </>}
                                    label={t('client_details_organization_model_number')}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <LabelValue
                                    value={activity?.organization?.organizationId}
                                    label={t('client_details_organization_id')}
                                /> 
                            </Grid>
                            <Grid item xs={12}>
                              <LabelValue 
                                  value={<div className='client_details_case_model_descriptions' dangerouslySetInnerHTML={{ __html: t(`caseModelDescriptions:${activity.sessionInfo?.modelId?.toString()}`) || ''}} />}
                                  label={t('caseModelDescriptions:model_description')}
                              />
                            </Grid>
                        </Grid> 
                        <Card className="client_details_organization_sub_section" elevation={0} radius="small">
                            <LabelValue
                                value={t('client_details_organization_max_hours_client')}
                                label={t('client_details_organization_date_range', {
                                    startDate: activity?.clientMaxHoursStartDate
                                        ? formatWithoutTimezone(activity.clientMaxHoursStartDate)
                                        : t('n_a', 'N/A'),
                                    endDate: activity?.clientMaxHoursEndDate
                                        ? formatWithoutTimezone(activity.clientMaxHoursEndDate)
                                        : t('n_a', 'N/A')
                                })}
                            />
                        </Card>
                    </Card>
                    {
                        activity?.sessionInfo?.modelId &&
                        <CaseModelInfoDialog open={showModelInfoDialog} caseModel={activity.sessionInfo.modelId}
                            onClose={() => setShowModelInfoDialog(false)} />
                    }
                </>
            }
        </Container>
    );
}